import { getYear } from 'date-fns';

import { Text } from '@/design-system';
import IgIcon from '../asset/svg/instagram-icon.svg';
import {
  PRIVACY_AGRREMENT_LINK,
  SERVICE_AGREEMENT_LINK,
} from '../utils/common';
import { FaqGtm } from '../utils/gtm/faq/faqGtm';
import { useHistoryMethod } from '../utils/hook/useHistoryMethod';
import useShowChannelTalkMessenger from '../utils/hook/useShowChannelTalkMessenger';

import { SVG } from './SVG';

const CUSTOMER_SERVICE_PHONE = {
  DISPLAY: '02.6408.0202',
  TEL_HREF: 'tel:02-6408-0202',
} as const;

const OPERATING_HOURS = [
  { days: '월·금요일', hours: '09:00 ~ 18:00 (점심 11:50 ~ 13:00)' },
  { days: '화-목요일', hours: '10:00 ~ 18:00 (점심 11:50 ~ 13:00)' },
  { days: '토·일·공휴일', hours: '휴무' },
] as const;

const CONTACT_EMAILS = [
  { label: '제휴 및 납품 문의', email: 'biz@uglyus.co.kr' },
  { label: '농산물 납품 및 긴급구출 제보', email: 'farm@uglyus.co.kr' },
  { label: 'B2B 대량구매 문의 연락처', email: 'jade@uglyus.co.kr' },
] as const;

const INSTAGRAM_LINK = 'https://www.instagram.com/uglyus.market/';

/**
 * 디자인 시스템 기반으로 갱신된 글로벌 푸터.
 * 고객센터 전화번호·운영시간·문의 진입점(채널톡/FAQ)과 회사 정보를 노출해
 * 고객센터 접근 경로를 제공합니다. 노출 페이지는 useToggleLayout의 showFooter로 제어합니다.
 */
const RenewalFooter = () => {
  return (
    <footer className="w-full bg-natural-white-100 pb-[72px] pt-[32px]">
      <div className="mx-auto flex w-full max-w-[1024px] flex-col gap-[24px] px-[16px]">
        <SupportSection />

        <div className="h-px w-full bg-soil-brown-300" />

        <CompanyInfoSection />
      </div>
    </footer>
  );
};

export default RenewalFooter;

const SupportSection = () => {
  const history = useHistoryMethod();
  const { showChannelTalkMessenger } = useShowChannelTalkMessenger();

  const onClickFaq = () => {
    new FaqGtm().click_faq();
    history.push('/faq');
  };

  return (
    <div className="flex w-full flex-col gap-[24px] sm:flex-row sm:items-start">
      <div className="flex flex-col gap-[10px] text-gray-800 sm:flex-1">
        <Text variant="h5-bold-140" as="h4">
          어글리어스 고객센터{' '}
          <a
            href={CUSTOMER_SERVICE_PHONE.TEL_HREF}
            className="text-sunshine-gold-600"
          >
            {CUSTOMER_SERVICE_PHONE.DISPLAY}
          </a>
        </Text>

        <div className="flex flex-col gap-[4px]">
          {OPERATING_HOURS.map(({ days, hours }) => (
            <div key={days} className="flex items-center gap-[8px]">
              <Text variant="h7-bold" as="span" className="w-[72px] shrink-0">
                {days}
              </Text>
              <Text variant="h7-regular-140" as="span">
                {hours}
              </Text>
            </div>
          ))}
        </div>
      </div>
      <div className="flex w-full gap-[8px] sm:w-auto">
        <button
          onClick={showChannelTalkMessenger}
          className="flex flex-1 items-center justify-center rounded-[10px] bg-soil-brown-700 p-[20px] sm:w-[180px] sm:flex-none"
          type="button"
        >
          <Text variant="h5-bold-100" as="span" className="text-white">
            문의하기
          </Text>
        </button>
        <button
          onClick={onClickFaq}
          className="flex flex-1 items-center justify-center rounded-[10px] border border-solid border-soil-brown-700 p-[20px] sm:w-[180px] sm:flex-none"
          type="button"
        >
          <Text variant="h5-bold-100" as="span" className="text-soil-brown-700">
            자주 묻는 질문
          </Text>
        </button>
      </div>
    </div>
  );
};

const CompanyInfoSection = () => {
  const copyrightText = `Copyright ⓒ ${getYear(new Date())} 주식회사 캐비지 All rights reserved.`;

  return (
    <div className="flex flex-col gap-[12px] text-gray-600">
      <SVG.Logo />

      <div className="text-[12px] leading-[1.6] sm:hidden">
        <p>상호명 : 주식회사 캐비지&nbsp;&nbsp;|&nbsp;&nbsp;대표 : 최현주</p>
        <p>사업자등록번호 : 572-81-02287</p>
        <p>소재지 : 서울특별시 영등포구 양평로 95, 6층(선앤유빌딩)</p>
        <p>통신판매업 신고번호 : 2021-화성동부-0302</p>
      </div>
      <p className="hidden text-[12px] leading-[1.6] sm:block">
        상호명 : 주식회사 캐비지&nbsp;&nbsp;|&nbsp;&nbsp;대표 :
        최현주&nbsp;&nbsp;|&nbsp;&nbsp;사업자등록번호 :
        572-81-02287&nbsp;&nbsp;|&nbsp;&nbsp;소재지 : 서울특별시 영등포구 양평로
        95, 6층(선앤유빌딩)&nbsp;&nbsp;|&nbsp;&nbsp;통신판매업 신고번호 :
        2021-화성동부-0302
      </p>

      <div className="flex flex-col items-start text-[12px] font-bold leading-[1.6]">
        {CONTACT_EMAILS.map(({ label, email }) => (
          <a key={email} href={`mailto:${email}`}>
            {label} : {email}
          </a>
        ))}
      </div>

      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-[16px]">
          <a href={SERVICE_AGREEMENT_LINK} target="_blank" rel="noreferrer">
            <Text variant="h7-bold" as="span">
              서비스 이용약관
            </Text>
          </a>
          <a href={PRIVACY_AGRREMENT_LINK} target="_blank" rel="noreferrer">
            <Text variant="h7-bold" as="span">
              개인정보처리방침
            </Text>
          </a>
          <Text variant="h7-regular-140" as="span" className="hidden sm:block">
            {copyrightText}
          </Text>
        </div>
        <a href={INSTAGRAM_LINK} target="_blank" rel="noreferrer">
          <img
            src={IgIcon.src}
            alt="어글리어스 인스타그램"
            className="size-[39px]"
          />
        </a>
      </div>

      <Text variant="h7-regular-140" as="p" className="sm:hidden">
        {copyrightText}
      </Text>
    </div>
  );
};
